import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@deere/isg.component-library";

interface Props {
  orgName: string;
  fullMQTT: any;
}

const DEFAULT_JOBSITE = "_diag";
const DEFAULT_SESSION = "_ping";

const SERVER_STAGES = [
  "httpReceive",
  "publishStart",
  "publishEnd",
  "brokerReceive",
  "mainDispatch",
  "workerStart",
  "workerEnd",
  "mainWorkerResult",
  "applyDecoded",
  "broadcast",
] as const;
type ServerStage = (typeof SERVER_STAGES)[number];

const STAGE_LABELS: Record<ServerStage, string> = {
  httpReceive: "HTTP handler entered",
  publishStart: "MQTT publish started",
  publishEnd: "MQTT publish complete (PUBACK)",
  brokerReceive: "broker delivered message back to backend",
  mainDispatch: "main thread dispatched to worker pool",
  workerStart: "worker thread entered decode",
  workerEnd: "worker thread exited decode",
  mainWorkerResult: "main thread received worker result",
  applyDecoded: "merged into in-memory org state",
  broadcast: "Socket.IO emit to org room",
};

interface PoolStats {
  total: number;
  busy: number;
  idle: number;
  pending: number;
  active: number;
}

interface EventLoopLag {
  meanMs: number;
  p50Ms: number;
  p99Ms: number;
  maxMs: number;
  windowMs: number;
}

interface PipelineStatus {
  workers: PoolStats | null;
  eventLoopLag: EventLoopLag;
  config: {
    flushIntervalMs: number;
    dbIntervalMs: number;
    dedupWindowMs: number;
    rateLimitMs: number;
  };
  queues: {
    dirtyOrgs: number;
    dbQueue: number;
    autonomyQueue: number;
  };
  uptimeMs: number;
}

interface StartResponse {
  correlationId: string;
  topic: string;
  serverNow: number;
  timings: Partial<Record<ServerStage, number>>;
  error?: string;
}

interface StagesResponse {
  correlationId: string;
  stages: Partial<Record<ServerStage, number>>;
  meta: Record<string, string | number>;
  pipeline: PipelineStatus;
  knownStages: ServerStage[];
  found: boolean;
}

type RunStatus = "idle" | "running" | "observed" | "complete" | "timeout" | "error";

interface RunState {
  status: RunStatus;
  correlationId: string;
  topic: string;
  jobsiteName: string;
  sessionName: string;
  tClickClient: number;
  tPostSentClient: number;
  tPostResponseClient: number;
  tObservedClient: number | null;
  serverNow: number;
  stages: Partial<Record<ServerStage, number>>;
  meta: Record<string, string | number>;
  pipeline?: PipelineStatus;
  error?: string;
}

const PipelineTimingTest: React.FC<Props> = ({ orgName, fullMQTT }) => {
  const [run, setRun] = useState<RunState | null>(null);
  const [liveStatus, setLiveStatus] = useState<PipelineStatus | null>(null);
  const runRef = useRef<RunState | null>(null);
  runRef.current = run;

  // Live pipeline status — polled at 1 Hz so the operator sees real-time
  // worker pool + event-loop pressure even when no test is running.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch(`/API/Diagnostics/Pipeline/Status`, {
          credentials: "same-origin",
        });
        if (!res.ok) return;
        const json: PipelineStatus = await res.json();
        if (!cancelled) setLiveStatus(json);
      } catch {
        /* ignore — transient socket errors are fine */
      }
    };
    void poll();
    const id = window.setInterval(poll, 1000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, []);

  // Detect arrival of the test message in the org snapshot. The pipeline
  // merges the topic `<org>/<job>/<ses>/pipelineTimingTest/<corrId>` into
  // `fullMQTT[org][job][ses].pipelineTimingTest[corrId]`.
  useEffect(() => {
    const r = runRef.current;
    if (!r || (r.status !== "running" && r.status !== "observed") || !orgName) return;
    if (r.tObservedClient !== null) return;
    const corrSlice =
      fullMQTT?.[orgName]?.[r.jobsiteName]?.[r.sessionName]?.pipelineTimingTest?.[r.correlationId];
    if (corrSlice && corrSlice.correlationId === r.correlationId) {
      const tObservedClient = Date.now();
      setRun((prev) => (prev ? { ...prev, tObservedClient, status: "observed" } : prev));
    }
  }, [fullMQTT, orgName, run?.status, run?.correlationId, run?.tObservedClient]);

  // After we've seen the value land in `fullMQTT`, give the backend ~250 ms
  // to flush its broadcast stage and then pull the full timing record.
  useEffect(() => {
    if (run?.status !== "observed") return;
    const corrId = run.correlationId;
    const timer = window.setTimeout(async () => {
      try {
        const res = await fetch(
          `/API/Diagnostics/PipelineTiming/${encodeURIComponent(corrId)}`,
          { credentials: "same-origin" },
        );
        const json: StagesResponse = await res.json();
        setRun((prev) =>
          prev && prev.correlationId === corrId
            ? {
                ...prev,
                stages: { ...prev.stages, ...(json.stages || {}) },
                meta: { ...prev.meta, ...(json.meta || {}) },
                pipeline: json.pipeline,
                status: "complete",
              }
            : prev,
        );
      } catch (err) {
        setRun((prev) =>
          prev && prev.correlationId === corrId
            ? { ...prev, status: "error", error: String(err) }
            : prev,
        );
      }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [run?.status, run?.correlationId]);

  // Hard timeout when the message never lands in fullMQTT.
  useEffect(() => {
    if (run?.status !== "running") return;
    const corrId = run.correlationId;
    const timer = window.setTimeout(async () => {
      const current = runRef.current;
      if (!current || current.correlationId !== corrId || current.status !== "running") return;
      try {
        const res = await fetch(
          `/API/Diagnostics/PipelineTiming/${encodeURIComponent(corrId)}`,
          { credentials: "same-origin" },
        );
        const json: StagesResponse = await res.json();
        setRun((prev) =>
          prev && prev.correlationId === corrId
            ? {
                ...prev,
                stages: { ...prev.stages, ...(json.stages || {}) },
                meta: { ...prev.meta, ...(json.meta || {}) },
                pipeline: json.pipeline,
                status: "timeout",
              }
            : prev,
        );
      } catch {
        setRun((prev) =>
          prev && prev.correlationId === corrId ? { ...prev, status: "timeout" } : prev,
        );
      }
    }, 10_000);
    return () => window.clearTimeout(timer);
  }, [run?.status, run?.correlationId]);

  const runTest = useCallback(async () => {
    if (!orgName) return;
    const tClickClient = Date.now();
    const jobsiteName = DEFAULT_JOBSITE;
    const sessionName = DEFAULT_SESSION;

    let resp: Response;
    try {
      resp = await fetch(`/API/Diagnostics/PipelineTiming/Start`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orgName, jobsiteName, sessionName }),
      });
    } catch (err) {
      setRun({
        status: "error",
        correlationId: "",
        topic: "",
        jobsiteName,
        sessionName,
        tClickClient,
        tPostSentClient: tClickClient,
        tPostResponseClient: Date.now(),
        tObservedClient: null,
        serverNow: tClickClient,
        stages: {},
        meta: {},
        error: String(err),
      });
      return;
    }

    const tPostResponseClient = Date.now();
    const json: StartResponse = await resp.json();
    if (json.error || !json.correlationId) {
      setRun({
        status: "error",
        correlationId: json.correlationId || "",
        topic: json.topic || "",
        jobsiteName,
        sessionName,
        tClickClient,
        tPostSentClient: tClickClient,
        tPostResponseClient,
        tObservedClient: null,
        serverNow: json.serverNow || tPostResponseClient,
        stages: json.timings || {},
        meta: {},
        error: json.error || "missing correlationId",
      });
      return;
    }
    setRun({
      status: "running",
      correlationId: json.correlationId,
      topic: json.topic,
      jobsiteName,
      sessionName,
      tClickClient,
      tPostSentClient: tClickClient,
      tPostResponseClient,
      tObservedClient: null,
      serverNow: json.serverNow,
      stages: json.timings || {},
      meta: {},
    });
  }, [orgName]);

  const ping = useMemo(() => {
    if (!run || run.tObservedClient === null) return null;
    return run.tObservedClient - run.tClickClient;
  }, [run]);

  // Estimate clock skew so we can place server stages on the client wall
  // clock too. Symmetric-network assumption is adequate for diagnostics.
  const clockOffset = useMemo(() => {
    if (!run || !run.stages.httpReceive) return null;
    const rtt = run.tPostResponseClient - run.tPostSentClient;
    const serverProc = run.serverNow - run.stages.httpReceive;
    const oneWay = Math.max(0, (rtt - serverProc) / 2);
    const httpReceiveClient = run.tPostSentClient + oneWay;
    return run.stages.httpReceive - httpReceiveClient;
  }, [run]);

  const status = liveStatus ?? run?.pipeline ?? null;

  return (
    <div
      style={{
        marginTop: 24,
        padding: 16,
        border: "1px solid #ccc",
        borderRadius: 6,
        background: "#fafafa",
      }}
    >
      <h3 style={{ margin: 0 }}>Pipeline Timing Test</h3>
      <p style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
        Round-trips a JSON message through the full stack:{" "}
        <code>click → POST → MQTT publish → broker → worker → state merge → Socket.IO → render</code>
        . Topic: <code>{orgName || "<orgName>"}/{DEFAULT_JOBSITE}/{DEFAULT_SESSION}/pipelineTimingTest/&lt;corrId&gt;</code>
      </p>

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 12 }}>
        <Button
          onClick={runTest}
          disabled={!orgName || run?.status === "running" || run?.status === "observed"}
        >
          {run?.status === "running" || run?.status === "observed"
            ? "Running…"
            : "Run pipeline timing test"}
        </Button>
        {!orgName && (
          <span style={{ color: "#c33", fontSize: 13 }}>
            Select an organization first.
          </span>
        )}
      </div>

      <PingDisplay ping={ping} run={run} />

      <ThreadPanel status={status} />

      <MainThreadPanel status={status} />

      {run && (
        <>
          <RunMeta run={run} clockOffset={clockOffset} />
          <StageTable run={run} clockOffset={clockOffset} />
          <ClientStageTable run={run} ping={ping} />
        </>
      )}
    </div>
  );
};

const PingDisplay: React.FC<{ ping: number | null; run: RunState | null }> = ({ ping, run }) => {
  const isComplete = run?.status === "complete" || run?.status === "observed";
  const color =
    ping === null
      ? "#999"
      : ping < 100
        ? "#0a7"
        : ping < 300
          ? "#a60"
          : "#c33";
  const subtitle = !run
    ? "click → render round-trip"
    : run.status === "running"
      ? "waiting for the message to come back…"
      : run.status === "timeout"
        ? "timed out — see stages below"
        : run.status === "error"
          ? `error: ${run.error}`
          : "click → render round-trip";

  return (
    <div
      style={{
        marginTop: 16,
        padding: 16,
        border: `2px solid ${color}`,
        borderRadius: 8,
        background: "#fff",
        display: "flex",
        alignItems: "baseline",
        gap: 16,
      }}
    >
      <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", letterSpacing: 1 }}>
        Ping
      </div>
      <div style={{ fontSize: 36, fontWeight: 600, color, fontVariantNumeric: "tabular-nums" }}>
        {ping === null ? "—" : `${ping} ms`}
      </div>
      <div style={{ fontSize: 13, color: "#666" }}>{subtitle}</div>
      {isComplete && run && (
        <div style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>
          POST RTT:{" "}
          <code>{run.tPostResponseClient - run.tPostSentClient} ms</code>
        </div>
      )}
    </div>
  );
};

const ThreadPanel: React.FC<{ status: PipelineStatus | null }> = ({ status }) => {
  if (!status) {
    return (
      <div style={{ marginTop: 12, fontSize: 13, color: "#888" }}>
        Worker pool: loading…
      </div>
    );
  }
  const w = status.workers;
  if (!w) {
    return (
      <div style={{ marginTop: 12, fontSize: 13, color: "#888" }}>
        Worker pool: not initialized.
      </div>
    );
  }
  const utilization = w.total === 0 ? 0 : (w.busy / w.total) * 100;
  const utilColor = utilization > 80 ? "#c33" : utilization > 50 ? "#a60" : "#0a7";
  const cells: Array<{ k: string; v: number; tone: string }> = [
    { k: "total", v: w.total, tone: "#444" },
    { k: "busy", v: w.busy, tone: utilColor },
    { k: "idle", v: w.idle, tone: "#0a7" },
    { k: "active tasks", v: w.active, tone: "#444" },
    { k: "pending", v: w.pending, tone: w.pending > 0 ? "#c33" : "#0a7" },
  ];
  return (
    <div
      style={{
        marginTop: 12,
        padding: 12,
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        Worker pool (decode threads)
      </div>
      <div
        style={{
          height: 10,
          background: "#eee",
          borderRadius: 4,
          overflow: "hidden",
          marginBottom: 8,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${utilization}%`,
            background: utilColor,
            transition: "width 0.5s ease",
          }}
        />
      </div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
        {cells.map((c) => (
          <div key={c.k} style={{ fontSize: 12 }}>
            <span style={{ color: "#666" }}>{c.k}: </span>
            <code style={{ color: c.tone, fontWeight: 600 }}>{c.v}</code>
          </div>
        ))}
        <div style={{ fontSize: 12 }}>
          <span style={{ color: "#666" }}>utilization: </span>
          <code style={{ color: utilColor, fontWeight: 600 }}>{utilization.toFixed(0)}%</code>
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: "#888" }}>
        queues — dirty orgs: <code>{status.queues.dirtyOrgs}</code> · db queue:{" "}
        <code>{status.queues.dbQueue}</code> · autonomy queue:{" "}
        <code>{status.queues.autonomyQueue}</code>
      </div>
    </div>
  );
};

const MainThreadPanel: React.FC<{ status: PipelineStatus | null }> = ({ status }) => {
  if (!status) return null;
  const lag = status.eventLoopLag;
  const meanColor = lag.meanMs > 50 ? "#c33" : lag.meanMs > 10 ? "#a60" : "#0a7";
  const p99Color = lag.p99Ms > 200 ? "#c33" : lag.p99Ms > 50 ? "#a60" : "#0a7";
  return (
    <div
      style={{
        marginTop: 8,
        padding: 12,
        background: "#fff",
        border: "1px solid #ddd",
        borderRadius: 4,
      }}
    >
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
        Main thread load (event-loop delay)
      </div>
      <div style={{ display: "flex", gap: 24, flexWrap: "wrap", fontSize: 12 }}>
        <div>
          <span style={{ color: "#666" }}>mean: </span>
          <code style={{ color: meanColor, fontWeight: 600 }}>{lag.meanMs.toFixed(2)} ms</code>
        </div>
        <div>
          <span style={{ color: "#666" }}>p50: </span>
          <code>{lag.p50Ms.toFixed(2)} ms</code>
        </div>
        <div>
          <span style={{ color: "#666" }}>p99: </span>
          <code style={{ color: p99Color, fontWeight: 600 }}>{lag.p99Ms.toFixed(2)} ms</code>
        </div>
        <div>
          <span style={{ color: "#666" }}>max: </span>
          <code>{lag.maxMs.toFixed(2)} ms</code>
        </div>
        <div>
          <span style={{ color: "#666" }}>uptime: </span>
          <code>{(status.uptimeMs / 1000).toFixed(0)} s</code>
        </div>
      </div>
      <div style={{ marginTop: 6, fontSize: 11, color: "#888" }}>
        Lag is how long the event loop is starved beyond its 20 ms sampling resolution. Sustained
        mean &gt; 10 ms or p99 &gt; 50 ms indicates the main thread is over-subscribed.
      </div>
    </div>
  );
};

const RunMeta: React.FC<{ run: RunState; clockOffset: number | null }> = ({ run, clockOffset }) => (
  <div style={{ marginTop: 16, fontSize: 12, color: "#444" }}>
    <div>
      <strong>correlationId:</strong> <code>{run.correlationId || "(none)"}</code>
    </div>
    <div>
      <strong>topic:</strong> <code>{run.topic || "(none)"}</code>
    </div>
    <div>
      <strong>status:</strong> {run.status}
      {run.error ? ` — ${run.error}` : ""}
    </div>
    {clockOffset !== null && (
      <div>
        <strong>est. clock skew (server − client):</strong> {clockOffset.toFixed(0)} ms
      </div>
    )}
    {run.status === "timeout" && (
      <div style={{ color: "#c33", marginTop: 6 }}>
        Timed out waiting for the message to land in <code>fullMQTT</code>. Check the missing rows
        in the table below — a missing <code>workerStart</code> typically means the pool is
        saturated; a missing <code>broadcast</code> typically means no client is in the org room.
      </div>
    )}
  </div>
);

const StageTable: React.FC<{ run: RunState; clockOffset: number | null }> = ({
  run,
  clockOffset,
}) => {
  const t0 = run.stages.httpReceive ?? null;
  const toClient = (serverTs: number): number | null =>
    clockOffset === null ? null : serverTs - clockOffset;
  let prev: number | null = null;
  return (
    <>
      <h4 style={{ margin: "16px 0 6px 0" }}>Server-side pipeline stages</h4>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#eee" }}>
            <th style={cellHdr}>stage</th>
            <th style={cellHdr}>server ts (ms)</th>
            <th style={cellHdr}>client ts (ms)</th>
            <th style={cellHdrR}>Δ prev</th>
            <th style={cellHdrR}>Δ start</th>
          </tr>
        </thead>
        <tbody>
          {SERVER_STAGES.map((stage, i) => {
            const ts = run.stages[stage];
            const missing = ts === undefined;
            const dPrev = missing || prev === null ? null : ts - prev;
            const dStart = missing || t0 === null ? null : ts - t0;
            const clientTs = !missing ? toClient(ts) : null;
            const dColor =
              dPrev === null
                ? "#999"
                : dPrev > 100
                  ? "#c33"
                  : dPrev > 20
                    ? "#a60"
                    : "#0a7";
            if (!missing) prev = ts;
            return (
              <tr key={stage} style={i % 2 ? { background: "#fafafa" } : undefined}>
                <td style={cell}>
                  <code style={{ color: missing ? "#c33" : undefined }}>{stage}</code>
                  <div style={{ color: "#888", fontSize: 11 }}>{STAGE_LABELS[stage]}</div>
                </td>
                <td style={cell}>
                  {missing ? <span style={{ color: "#c33" }}>(not recorded)</span> : ts}
                </td>
                <td style={cell}>{clientTs === null ? "—" : clientTs.toFixed(0)}</td>
                <td style={{ ...cellR, color: dColor }}>
                  {dPrev === null ? "—" : `+${dPrev}`}
                </td>
                <td style={cellR}>{dStart === null ? "—" : `+${dStart}`}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </>
  );
};

const ClientStageTable: React.FC<{ run: RunState; ping: number | null }> = ({ run, ping }) => {
  const rows: Array<{ label: string; ts: number | null; sub: string }> = [
    { label: "tClickClient", ts: run.tClickClient, sub: "operator clicked Run" },
    {
      label: "tPostResponseClient",
      ts: run.tPostResponseClient,
      sub: "POST returned (publish complete)",
    },
    {
      label: "tObservedClient",
      ts: run.tObservedClient,
      sub: "value visible in fullMQTT (= rendered)",
    },
  ];
  return (
    <>
      <h4 style={{ margin: "12px 0 6px 0" }}>Client-side stages</h4>
      <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "#eee" }}>
            <th style={cellHdr}>stage</th>
            <th style={cellHdr}>client ts (ms)</th>
            <th style={cellHdrR}>Δ click</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const dClick = r.ts === null ? null : r.ts - run.tClickClient;
            return (
              <tr key={r.label} style={i % 2 ? { background: "#fafafa" } : undefined}>
                <td style={cell}>
                  <code>{r.label}</code>
                  <div style={{ color: "#888", fontSize: 11 }}>{r.sub}</div>
                </td>
                <td style={cell}>
                  {r.ts === null ? <span style={{ color: "#c33" }}>(not yet)</span> : r.ts}
                </td>
                <td style={cellR}>{dClick === null ? "—" : `+${dClick}`}</td>
              </tr>
            );
          })}
          <tr style={{ background: "#fff8e0" }}>
            <td style={{ ...cell, fontWeight: 600 }}>
              <code>ping</code>
              <div style={{ color: "#888", fontSize: 11 }}>
                tObservedClient − tClickClient
              </div>
            </td>
            <td style={cell}>—</td>
            <td style={{ ...cellR, fontWeight: 600 }}>
              {ping === null ? "—" : `+${ping}`}
            </td>
          </tr>
        </tbody>
      </table>
    </>
  );
};

const cellHdr: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid #ccc",
  textAlign: "left",
  fontWeight: 600,
};
const cellHdrR: React.CSSProperties = { ...cellHdr, textAlign: "right" };
const cell: React.CSSProperties = {
  padding: "6px 8px",
  borderBottom: "1px solid #eee",
  verticalAlign: "top",
};
const cellR: React.CSSProperties = { ...cell, textAlign: "right", fontVariantNumeric: "tabular-nums" };

export default PipelineTimingTest;
